import { AppHTTPService } from '@/api/http-service'
import { FieldConfig } from '@flatfile/api'
import type { MappingRule } from '@flatfile/mapping'
import { useQuery } from '@tanstack/react-query'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'
import { useParams } from 'react-router-dom'

export type Sheet = {
  id: string
  name: string
  fileId: string
  createdAt: string
  updatedAt: string
  schema: {
    fields: FieldConfig[]
  }
  rules?: MappingRule[]
  enrichments?: Array<{
    id?: string
    key: string
    name: string
    type: 'research' | 'prompt' | 'translate'
    config: {
      [key: string]: any
    }
    runInBackground?: boolean
  }>
  metadata: {
    [key: string]: any
  }
}

interface SheetContextType {
  sheet: Sheet
  isLoading: boolean
  error: Error | null
}

const SheetContext = createContext<SheetContextType | undefined>(undefined)

export function SheetProvider({ children }: PropsWithChildren) {
  const { sheetId } = useParams()

  const {
    data: sheetData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['sheets', sheetId],
    queryFn: async () => {
      const response = await AppHTTPService.client.sheets.get(sheetId)
      return response
    },
    enabled: !!sheetId,
  })

  const value = useMemo(
    () => ({
      sheet: sheetData?.data,
      isLoading,
      error,
    }),
    [sheetData?.data, isLoading, error]
  )

  return <SheetContext.Provider value={value}>{children}</SheetContext.Provider>
}

export function useSheet() {
  const context = useContext(SheetContext)
  if (!context) {
    throw new Error('useSheet must be used within a SheetProvider')
  }
  return context
}
